import {
    FavoritesIcon,
    LeftArrowIcon,
    HeartIcon,
    AudioPlayer,
    PlayIcon,
    VoiceSquare,
} from '../components';
import { Link, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSingleCatechesis, toggleFavorite } from '../utilities/api';
import { mapSingleCatechesisData } from '../utilities/dataTransform';
import { Skeleton } from "../components/ui/skeleton"; // Import Skeleton component
import TrinityAudioPlayer from './TrinityAudioPlayer';
function SingleCatechesis({ }) {
    const { id } = useParams();
    const [data, setData] = useState(null); // Set null initially
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPlayer, setShowPlayer] = useState(false);
    const [showTrinity, setShowTrinity] = useState(false);
    const [progressPercentage, setProgressPercentage] = useState(0);
    const audioRef = useRef(null);

    const isValidAudioUrl = (url) =>
        typeof url === "string" && /\.(mp3|ogg|wav)$/i.test(url);

    useEffect(() => {
        if (!id) return;
        const controller = new AbortController();
        const signal = controller.signal;
        setLoading(true);
        setData(null);
        const fetchData = async () => {
            try {
                const result = await fetchSingleCatechesis(id, signal);
                if (result?.data) {
                    const transformedData = mapSingleCatechesisData(result.data, result.included);
                    setData(transformedData);
                } else {
                    setError("No data found.");
                }
            } catch (err) {
                console.error("Fetch Error:", err);
                setError(`Failed to fetch data: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        return () => controller.abort();
    }, [id]);
    const handleToggleFavorite = async (id) => {
        try {
            if (!data || data.id !== id) {
                console.error(" Error: Invalid data state.");
                return;
            }
            // Step 1: Optimistically update UI
            setData((prevData) => prevData ? { ...prevData, favorite: !prevData.favorite } : null);
            // Step 2: Call API to update favorite status
            await toggleFavorite('catecheses', id);
        } catch (error) {
            console.error(" Failed to update favorite:", error);
            //  Step 3: Revert UI if API call fails
            setData((prevData) => prevData ? { ...prevData, favorite: !prevData.favorite } : null);
        }
    };
    const handlePlay = () => {
        if (isValidAudioUrl(data?.audio)) {
            setShowTrinity(false);
            setShowPlayer(true);
            setTimeout(() => audioRef.current?.play(), 100);
        } else {
            setShowPlayer(false);
            setShowTrinity(true);
        }
    };
    return (
        <div className='w-full'>
            <div className="h-full">
                <div className="flex flex-col h-full xl:flex-row">
                    <div className="w-full border-b border-gray-100 xl:w-1/3 xl:border-r xl:border-b-0">
                        <div className="flex justify-between px-6 py-8 pb-8 ml-0 mr-6 xl:ml-6 xl:mr-12">
                            <div>
                                <Link className="flex items-center gap-3 group" to={`/learn-the-faith/${data?.courseId}`}>
                                    <div className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11">
                                        <LeftArrowIcon className="text-gray-700 group-hover:text-gold-500" />
                                    </div>
                                    <h5 className="font-normal text-base leading-5.5 text-gray-600 group-hover:text-gold-500">
                                        {loading || !data ? (
                                            <Skeleton className="w-40 h-5 rounded" />
                                        ) : (
                                            <>
                                                Back To {''}
                                                {Array.isArray(data?.courseList) && data.courseList.length > 0 ? (
                                                    data.courseList.map((course, index) => (
                                                        <span key={index} className="text-gray-600 group-hover:text-gold-500">
                                                            {course}{index < data.courseList.length - 1 ? ', ' : ''}
                                                        </span>
                                                    ))
                                                ) : (
                                                    "Back to Group of Catechesis title"
                                                )}
                                            </>
                                        )}
                                    </h5>
                                </Link>
                            </div>
                        </div>
                        <div className="px-6 py-8 pb-8 ml-0 mr-6 xl:ml-6 xl:mr-12">
                            <div className="flex-row">
                                <div>
                                    <h2 className="pb-3 text-2xl font-bold text-gray-900">
                                        {loading || !data ? <Skeleton className="w-3/4 h-8 rounded" /> : data?.title}
                                    </h2>
                                </div>
                                <div className=''>
                                    {loading || !data ? (
                                        // Show only skeleton (without "From:" text)
                                        <Skeleton className="inline-block w-40 h-3 rounded" />
                                    ) : (
                                        <p className="text-sm font-medium text-gray-900">
                                            From :{' '}
                                            {Array.isArray(data?.courseList) && data.courseList.length > 0 ? (
                                                data.courseList.map((course, index) => (
                                                    <span key={index} className="text-sm font-medium text-gold-300">
                                                        {course}{index < data.courseList.length - 1 ? ', ' : ''}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-sm font-medium text-gold-300">Catechesis Group Name</span>
                                            )}
                                        </p>
                                        
                                    )}
                                </div>
                                <div
                                onClick={handlePlay}
                                className="cursor-pointer w-full max-w-[320px] bg-gold-300 rounded-[50px] p-2 flex items-center justify-between my-8"
                            >
                                <div className="flex items-center justify-center rounded-full bg-gold-200 md:h-11 md:w-11 h-9 w-9">
                                    {showPlayer ? <VoiceSquare progress={progressPercentage / 100} /> : <PlayIcon className="text-gray-0" />}
                                </div>
                                <div className="flex items-center justify-center flex-grow select-none">
                                    <p className="text-lg font-bold text-gray-0">Play Audio</p>
                                </div>
                            </div>
                            {showPlayer && (
                                <div className="mt-4">
                                    <AudioPlayer
                                        src={data.audio}
                                        title={data.title}
                                        excerpt="Catechesis Audio"
                                        onClose={() => setShowPlayer(false)}
                                        audioRef={audioRef}
                                        onProgressUpdate={setProgressPercentage}
                                    />
                                </div>
                            )}

                            {showTrinity && (
                                <div className="mt-4">
                                    <TrinityAudioPlayer selector=".custom-content" langKey="en" />
                                </div>
                            )}
                                
                                <div></div>
                            </div>
                        </div>
                        <div className='border-b border-gray-100'></div>
                        {(data?.links?.previous?.length > 0 || data?.links?.next?.length > 0) && (
                            <div className="px-6 py-8 pt-8 ml-0 mr-6 xl:ml-6 xl:mr-12">
                                <h4 className="pb-3 text-base font-semibold text-gray-900">Other Posts from the Source:</h4>
                                {/* Previous */}
                                {data?.links?.previous?.length > 0 && (
                                    <div>
                                        <h6 className="pb-2 text-sm font-medium text-gray-900">Previous:</h6>
                                        <ul>
                                            {data.links.previous.map((item) => (
                                                <li key={item.id} className="pb-2 text-sm font-medium text-gold-300">
                                                    <Link to={`/learn-the-faith/catechesis/${item.slug}`} className="hover:underline">
                                                        {item.title}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {/* Next */}
                                {data?.links?.next?.length > 0 && (
                                    <div className="mt-4">
                                        <h6 className="pb-2 text-sm font-medium text-gray-900">Next:</h6>
                                        <ul>
                                            {data.links.next.map((item) => (
                                                <li key={item.id} className="pb-2 text-sm font-medium text-gold-300">
                                                    <Link to={`/learn-the-faith/catechesis/${item.slug}`} className="hover:underline">
                                                        {item.title}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                        {/* <div className="px-6 py-8 pt-8 ml-0 mr-6 xl:ml-6 xl:mr-12">
                            <h4 className="pb-3 text-base font-semibold text-gray-900">Source:</h4>
                            <div className="p-4 bg-gold-50 rounded-xl">
                                <p className="text-sm font-medium text-gray-900">This Catechesis appears in <a href='#' className="text-sm font-medium text-gold-300">“Name of the book” </a></p>
                            </div>
                        </div>
                        <div className="px-6 py-8 pt-8 ml-0 mr-6 xl:ml-6 xl:mr-12">
                            <h4 className="pb-3 text-base font-semibold text-gray-900">Related Content:</h4>
                            <div className="flex flex-row">
                                <div className="">
                                    <img src="https://s3-alpha-sig.figma.com/img/99ca/aa14/b8709e028f9fae8d2cb436406905cd4e?Expires=1735516800&Key-Pair-Id=APKAQ4GOSFWCVNEHN3O4&Signature=Q-G-OOkIB2D83NJdT~xK8-Ks6yt12ThCe6Lor9j4HnCndaIZbSWsFeob~~M~oKUjwWdbr9LVlHl3CGYnx2BskXIGxG5G6hjXYwHq6o6RReWcfg-l91tZ-Z9aNRBLY9c8QYiM0KnkQS7GnnQGxIVMBDyoqoP16Wv1kjwwWdj-X2TSB9DvFsfQzQoGi5z5v6B0ugjLdPmT8SjNZ9ybbuLx7LBZPj4nJ6HGYLxseUFCJnHGz8WL6caR8L4DvLcJFzok6B37x-~ApeMZiimngyWZZYDxiS-~2vIoeTnfyXEWP-SD19qTGTsGIZnhG~XpvHWwS0I7ogRKXkDaD1QGdlAIyw__" alt="image" className="h-full bg-gray-200 max-w-20 rounded-bl-xl rounded-tl-xl" />
                                </div>
                                <div className="flex flex-col w-full px-4 py-3 bg-gold-50 rounded-br-xl rounded-tr-xl">
                                    <p className="text-base font-semibold leading-5 text-left text-gray-900">Prayer Title</p>
                                    <span className="mt-1 text-xs font-normal leading-4 text-left text-gray-600 mb-2.5">Several sentences about Prayer</span>
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-wrap gap-2">
                                            <span className="px-2 py-1 text-xs font-medium leading-4 bg-white-50 text-gold-500 rounded-27">Easter</span>
                                            <span className="px-2 py-1 text-xs font-medium leading-4 bg-white-50 text-gold-500 rounded-27">For the Home</span>
                                        </div>

                                        <a href="#" className="flex items-center justify-center rounded-full bg-gold-50 h-11 w-11 group">
                                            <HeartIcon className="text-gray-700 w-7 h-7 group-hover:text-gold-500" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div> */}
                    </div>
                    <div className="px-6 py-8 ml-0 mr-0 xl:w-2/3 xl:ml-12 xl:mr-6">
                        <div className="justify-end hidden gap-2 mb-4 xl:flex">
                            {
                                loading || !data ? (
                                    <Skeleton className="transition-colors rounded w-7 h-7" /> // Title Skeleton
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault(); // Prevent default behavior to stop reload
                                                e.stopPropagation(); // Prevent event bubbling
                                                handleToggleFavorite(data.id, e);
                                            }}
                                            className="focus:outline-none"
                                        >
                                            <FavoritesIcon
                                                filled={Boolean(data?.favorite)}
                                                className={`w-7 h-7 transition-colors duration-200 ${data?.favorite ? 'text-red-100' : 'text-gray-500 hover:text-red-100'
                                                    }`}
                                            />
                                        </button>
                                    </>
                                )
                            }
                        </div>
                        <div className="w-full h-full">
                            {loading || !data ? (
                                <div className="space-y-4">
                                    {/* <Skeleton className="w-1/2 h-8 rounded" /> */}
                                    <Skeleton className="h-[500px] w-full rounded" />
                                </div>
                            ) : (
                                <>
                                    {/* <h2 className="pb-6 text-lg font-bold text-gray-900">{data?.title}</h2> */}
                                    <div
                                        className="text-lg text-gray-700 custom-content my-audio-content"
                                        dangerouslySetInnerHTML={{ __html: data?.content }} />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
export default SingleCatechesis;
